import { useEffect } from 'react';
import { useDomainConfig } from './useDomainConfig';

// Extend window object to include Microsoft Clarity
declare global {
  interface Window {
    clarity: (action: string, ...args: any[]) => void;
  }
}

export const useMicrosoftClarity = () => {
  const config = useDomainConfig();

  useEffect(() => {
    const { analytics } = config;
    
    // Only initialize if tracking is enabled and we have a Clarity ID
    if (!analytics.trackingEnabled || !analytics.microsoftClarityId) {
      return;
    }

    // Check if script is already loaded
    const existingScript = document.querySelector(
      `script[src*="clarity.ms/tag/${analytics.microsoftClarityId}"]`
    );

    if (existingScript) {
      return; // Already loaded
    }

    // Initialize Microsoft Clarity
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${analytics.microsoftClarityId}`;
    
    // Add the inline script for initialization
    const inlineScript = document.createElement('script');
    inlineScript.type = 'text/javascript';
    inlineScript.innerHTML = `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "${analytics.microsoftClarityId}");
    `;

    // Append scripts to head
    document.head.appendChild(inlineScript);
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      // Remove the scripts on cleanup
      if (inlineScript.parentNode) {
        inlineScript.parentNode.removeChild(inlineScript);
      }
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [config.analytics.microsoftClarityId, config.analytics.trackingEnabled]);

  // Helper functions for tracking events
  const trackEvent = (eventName: string, customData?: Record<string, any>) => {
    if (window.clarity && config.analytics.trackingEnabled) {
      if (customData) {
        window.clarity('event', eventName, customData);
      } else {
        window.clarity('event', eventName);
      }
    }
  };

  const trackVideoDownload = (platform: string, videoTitle: string, format: string) => {
    trackEvent('video_download', {
      platform: platform,
      video_title: videoTitle,
      format: format,
      category: 'Download'
    });
  };

  const trackVideoSearch = (url: string) => {
    let platform = 'unknown';
    if (url.includes('youtube.com') || url.includes('youtu.be')) platform = 'youtube';
    else if (url.includes('tiktok.com')) platform = 'tiktok';
    else if (url.includes('twitter.com') || url.includes('x.com')) platform = 'twitter';
    else if (url.includes('facebook.com')) platform = 'facebook';
    else if (url.includes('instagram.com')) platform = 'instagram';
    else if (url.includes('vimeo.com')) platform = 'vimeo';
    else if (url.includes('bilibili.com')) platform = 'bilibili';
    else if (url.includes('xiaohongshu.com') || url.includes('xhscdn.com')) platform = 'xiaohongshu';
    
    trackEvent('video_search', {
      platform: platform,
      search_term: url,
      category: 'Search'
    });
  };

  const trackPageView = (pagePath: string, pageTitle: string) => {
    if (window.clarity && config.analytics.trackingEnabled) {
      window.clarity('set', 'page_path', pagePath);
      window.clarity('set', 'page_title', pageTitle);
    }
  };

  const setUserIdentifier = (userId: string) => {
    if (window.clarity && config.analytics.trackingEnabled) {
      window.clarity('identify', userId);
    }
  };

  const setCustomTag = (key: string, value: string) => {
    if (window.clarity && config.analytics.trackingEnabled) {
      window.clarity('set', key, value);
    }
  };

  return {
    trackEvent,
    trackVideoDownload,
    trackVideoSearch,
    trackPageView,
    setUserIdentifier,
    setCustomTag,
    isTrackingEnabled: config.analytics.trackingEnabled
  };
};
